import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@mui/material';
import jsQR from 'jsqr';
import Header from '../components/Header';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function PointReading({ user }) {
  const navigate = useNavigate();
  // useRefで保持すると、再描画の影響を受けずに保持される
  // <video> DOM要素を直接触るための参照
  const videoRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  // マイフレーム処理するのではなく、一定時間を空けてから処理するようにする
  const scanTimeoutIdRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  const [qrcode, setQrcode] = useState(null);
  const [stream, setStream] = useState(null);
  const [message, setMessage] = useState('');

  // 予約した処理をキャンセルする
  const clearLoopTimers = () => {
    // requestAnimationFrameの予約をキャンセル
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    // setTimeoutの予約をキャンセル
    if (scanTimeoutIdRef.current) {
      clearTimeout(scanTimeoutIdRef.current);
      scanTimeoutIdRef.current = null;
    }
  };

  // useCallbackとは
  // →　React の再レンダーで関数が毎回作り直されるのを防ぐ仕組み
  // →　第2引数に「依存する値」を書いておくと、その値が変わったときだけ新しい関数を作る

  // カメラ停止
  // track.stop() で物理的にカメラを止めても、<video> が参照を持ったままだと見た目上も裏側的にも中途半端なので、合わせて srcObject=null にするのがベスト
  const stopCamera = useCallback(() => {
    // QRコードスキャン用のループ予約を全部解除
    clearLoopTimers();

    if (stream) {
      // stream.getTracks()　→ MediaStreamのトラックを取得
      // ストリーム内の すべてのトラック（カメラ/マイク）を停止
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    // streamが変更された時のみこの関数を作り直す(古いstreamを使用しないようにするため)
  }, [stream]);

  // ページ遷移などでコンポーネントが破棄される際にもカメラを停止する
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // QR解析
  // 今のカメラフレームを Canvas に描いて
  // jsQR で QR を解析して
  // ヒットすれば Firestore 照合 → 成功なら stopCamera()
  const handleQRCode = async () => {
    if (!videoRef.current || !ctxRef.current || !canvasRef.current) return;
    if (qrcode) return; // 既に成功したスキャンがある時は二重実行を防止

    try {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;

      // 現在のフレームをCanvasへ描画
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const code = jsQR(imageData.data, canvas.width, canvas.height);

      if (!code) return; // 認識できなければスルー（ループ継続）

      const scanned = (code.data || '').trim();
      if (!scanned) {
        setMessage('QRコードを認識できませんでした');
        return; // ループ継続
      }


      setQrcode(scanned); // 重複実行抑止用

      const userId = scanned; // 仕様に合わせて読み替え
      const shopId = user?.uid;

      if (!shopId) {
        setMessage('ログイン情報が確認できません。再ログイン後にお試しください。');
        setQrcode(null);
        return; // ループ継続（ボタンでやり直し想定）
      }

      // Firestore取得
      const cardRef = doc(db, `users/${userId}/cards/${shopId}`);
      const cardSnap = await getDoc(cardRef);
      const shopRef = doc(db, `customers/${shopId}`);
      const shopSnap = await getDoc(shopRef);

      if (cardSnap.exists()) {
        // 成功時のみ停止して遷移
        stopCamera();
        navigate('/pointUsing', {
          state: {
            cardDocData: cardSnap.data(),
            shopDocData: shopSnap.data(),
            userId,
            shopId,
          },
        });
      } else {
        setMessage('該当のカードが見つかりませんでした');
        setQrcode(null); // 次のスキャンを許可
      }
    } catch (e) {
      console.error(e);
      setMessage('正しいQRコードを読み取ってください');
      setQrcode(null); // 次のスキャンを許可
    }
  };

  // スキャンのループ処理
  const animate = () => {
    handleQRCode();
    // 以下で2段階の予約をしている
    // setTimeoutもrequestAnimationFrameもキャンセルできるように、予約番号はuseRefで保持している
    // setTimeoutで0.2秒後に処理を実行するように予約(予約1)
    scanTimeoutIdRef.current = setTimeout(() => {
      // requestAnimationFrame → 次の描画前に関数の実行を予約するブラウザのAPI(予約2)
      animationFrameIdRef.current = requestAnimationFrame(animate);
    }, 200);
  };

  // カメラ起動
  const startCamera = async () => {
    // 連続タップによる二重起動の防止
    if (stream) return;

    try {
      setMessage('');
      setQrcode(null);
      clearLoopTimers();

      // 背面カメラがあれば背景カメラを使用する。なければ他のカメラを使用してもOK。　という設定
      const constraints = { video: { facingMode: { ideal: 'environment' } } };
      // MediaStreamオブジェクトを生成　→ このオブジェクトを<video> の srcObject に渡すと、カメラ映像が画面に映る
      // 1つの MediaStream は複数の「トラック」を持っている
      // ・映像トラック（video）
      // ・音声トラック（audio）stream.getTracks()
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!videoRef.current) return;

      // <video> には srcObject という特別なプロパティがあり、ここに MediaStream を直接渡すことで
      // カメラ映像がリアルタイムで <video> に表示される
      // カメラ映像を <video> に映すだけなら　videoRef.current.srcObject = newStream;だけで十分
      // setStream(newStream) して state　で管理する理由は
      // ・停止処理で track.stop() するときに参照できるようにするため
      // ・UI側で「カメラがONかOFFか」をわかりやすく管理するため
      videoRef.current.srcObject = newStream;
      setStream(newStream);

      // 再生&メタデータ読み込みを待つ
      await videoRef.current.play();
      await new Promise((resolve) => {
        if (videoRef.current.readyState >= 1 && videoRef.current.videoWidth) return resolve();
        const onLoadedMetadata = () => {
          videoRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
          resolve();
        };
        videoRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
      });

      // Canvas初期化（端末により0が返るケースへのフォールバック付き）
      const width = videoRef.current.videoWidth || 640;
      const height = videoRef.current.videoHeight || 480;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      canvasRef.current = canvas;
      ctxRef.current = ctx;

      // スキャン開始
      animate();
    } catch (error) {
      console.error('Error accessing camera:', error);
      setMessage('カメラにアクセスできませんでした。権限をご確認ください。');
    }
  };

  return (
    <div>
      <Header text={'ポイント付与/使用'} />
      <div>
        <p>お客様のQRコードを写してください</p>
        {message && <p style={{ color: '#d32f2f', marginTop: 8 }}>{message}</p>}

        <div
          style={{
            width: '90%',
            height: 300,
            position: 'relative',
            overflow: 'hidden',
            border: 'solid 1px',
            margin: '0 auto',
          }}
        >
          <video
            ref={videoRef}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minHeight: '100%',
              minWidth: '100%',
            }}
            playsInline
            muted
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 25 }}>
          <Button variant="contained" color="primary" onClick={startCamera}>
            読取開始
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setMessage('カメラを停止しました');
              stopCamera();
              setQrcode(null);
            }}
          >
            停止
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PointReading;
